import React, { useMemo } from 'react'
import type { QuarterKey } from '../../../utils/regionalClientPerformanceUtils'
import type { RegionalInvoicedTrendRow } from '../../../utils/regionalInvoicedTrendsUtils'
import {
  sortTrendRows,
  filterTrendRows,
  groupTrendsByGeography,
  getTrendsKpiPayload,
  regionDotClass,
  vatSubline,
  trendNum,
} from '../../../utils/regionalInvoicedTrendsUtils'

export interface RegionalInvoicedTrendSectionProps {
  records: RegionalInvoicedTrendRow[]
  selectedYear: string
  selectedQuarter: QuarterKey
  selectedMonth: string
  loading?: boolean
}

const SkeletonBlock: React.FC<{ className?: string }> = ({
  className = 'h-24',
}) => <div className={`rounded-xl bg-slate-200/80 animate-pulse ${className}`} />

/**
 * Invoiced net revenue KPIs + regional comparison (all regions; FY/Q/month from page).
 */
export function RegionalInvoicedTrendSection({
  records,
  selectedYear,
  selectedQuarter,
  selectedMonth,
  loading,
}: RegionalInvoicedTrendSectionProps) {
  const sorted = useMemo(() => sortTrendRows(records), [records])

  const filtered = useMemo(
    () =>
      filterTrendRows(
        sorted,
        selectedYear,
        selectedQuarter,
        selectedMonth,
        'all'
      ),
    [sorted, selectedYear, selectedQuarter, selectedMonth]
  )

  const kpi = useMemo(() => getTrendsKpiPayload(filtered), [filtered])
  const regionMap = useMemo(() => groupTrendsByGeography(filtered), [filtered])

  const regionNames = Object.keys(regionMap).sort()

  if (loading) {
    return (
      <div className="space-y-4">
        <SkeletonBlock className="h-32" />
        <SkeletonBlock className="h-48" />
      </div>
    )
  }

  if (records.length === 0) return null

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 shadow-md p-4">
        <h2 className="text-sm font-semibold text-slate-800 mb-4">
          Regional invoiced revenue trends
        </h2>

        {!kpi ? (
          <p className="text-sm text-slate-500 py-6 text-center">
            No trend rows for this period.
          </p>
        ) : (
          <>
            <div className="rounded-xl border border-slate-200 shadow-sm p-5 mb-6 border-l-4 border-l-emerald-500">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">
                Combined regional revenue
              </p>
              <p className="text-2xl font-bold tabular-nums text-slate-900">
                ${(kpi.regional_invoiced_usd_net / 1000).toFixed(0)}K
              </p>
              <p className="text-[12px] font-semibold tabular-nums mt-2 leading-relaxed flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span
                  className={
                    kpi.mom_change_percent >= 0
                      ? 'text-emerald-700'
                      : 'text-rose-700'
                  }
                >
                  MoM {kpi.mom_change_percent >= 0 ? '+' : ''}
                  {kpi.mom_change_percent.toFixed(1)}%
                </span>
                <span className="text-slate-400 font-normal select-none" aria-hidden>
                  ·
                </span>
                <span
                  className={
                    kpi.yoy_change_percent >= 0
                      ? 'text-violet-700'
                      : 'text-rose-700'
                  }
                >
                  YoY {kpi.yoy_change_percent >= 0 ? '+' : ''}
                  {kpi.yoy_change_percent.toFixed(1)}%
                </span>
                <span className="text-slate-400 font-normal select-none" aria-hidden>
                  ·
                </span>
                <span className="text-amber-800">
                  3MA ${(kpi.three_month_moving_avg / 1000).toFixed(0)}K
                </span>
              </p>
            </div>

            {regionNames.length > 1 && (
              <>
                <h3 className="text-sm font-semibold text-slate-800 mt-5 mb-3">
                  Regional revenue comparison
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {regionNames.map(region => {
                    const data = regionMap[region] ?? []
                    const regionRevenue = data.reduce(
                      (s, item) =>
                        s + trendNum(item.regional_invoiced_usd_net),
                      0
                    )
                    const regionCurrency = data[0]?.region_currency || 'USD'
                    const vatRaw = data[0]?.vat_treatment || ''
                    const vatLine = vatSubline(vatRaw)

                    return (
                      <div
                        key={region}
                        className="flex items-center justify-between p-3 rounded-lg bg-white border border-slate-200 shadow-sm"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className={`w-3 h-3 rounded-full shrink-0 ${regionDotClass(region)}`}
                          />
                          <div className="min-w-0">
                            <div className="font-medium text-slate-900 truncate">
                              {region}
                            </div>
                            <div className="text-xs text-slate-500">
                              {regionCurrency}
                              {vatLine ? (
                                <span className="text-amber-800 ml-1">
                                  · {vatLine}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        <div className="text-right shrink-0 pl-2">
                          <div className="font-semibold text-slate-900 tabular-nums">
                            ${(regionRevenue / 1000).toFixed(0)}K
                          </div>
                          <div className="text-xs text-slate-500">
                            Net revenue
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}
